import React, { useEffect, useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { Search, RefreshCw, ChevronLeft, ChevronRight, Copy, Download } from 'lucide-react';
import { safeApolloHref } from '../../utils/safeApolloHref';

const PagedJobReportsTable = () => {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  // State
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [jobTypeFilter, setJobTypeFilter] = useState(''); // '', 'apollo', 'lead_finder'
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [copiedId, setCopiedId] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [copiedAmpleLeadsId, setCopiedAmpleLeadsId] = useState(null);
  const [forceFailing, setForceFailing] = useState(null); // job_id being force-failed
  const [recheckingJobId, setRecheckingJobId] = useState(null); // job_id being rechecked
  const [deductingCreditsJobId, setDeductingCreditsJobId] = useState(null); // job_id being retroactively charged
  const [showForceFailModal, setShowForceFailModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [failureReason, setFailureReason] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchPage = async (p = page, sBy = sortBy, sDir = sortDir, size = pageSize, query = search, sd = startDate, ed = endDate, jt = jobTypeFilter) => {
    setLoading(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) throw new Error('No admin token');
      const startISO = new Date(sd + 'T00:00:00Z').toISOString();
      const endISO = new Date(ed + 'T23:59:59Z').toISOString();
      const url = new URL(`${API_BASE_URL}/api/admin/reports/jobs/paged`);
      url.searchParams.set('start_date', startISO);
      url.searchParams.set('end_date', endISO);
      url.searchParams.set('page', String(p));
      url.searchParams.set('page_size', String(size));
      url.searchParams.set('sort_by', sBy);
      url.searchParams.set('sort_dir', sDir);
      if (query && query.trim()) url.searchParams.set('q', query.trim());
      if (jt) url.searchParams.set('job_type', jt);
      const resp = await fetch(url.toString(), { headers: { 'Authorization': `Bearer ${adminToken}` } });
      if (!resp.ok) throw new Error('Failed to fetch job reports');
      const data = await resp.json();
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setPageSize(data.page_size || 50);
      setSortBy(data.sort_by || sBy);
      setSortDir(data.sort_dir || sDir);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPage(1, 'created_at', 'desc', 50, ''); }, []);

  // Exports the full date range with the filters currently on screen (not just this page).
  const exportXlsx = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) throw new Error('No admin token');
      const startISO = new Date(startDate + 'T00:00:00Z').toISOString();
      const endISO = new Date(endDate + 'T23:59:59Z').toISOString();
      const url = new URL(`${API_BASE_URL}/api/admin/reports/jobs/export`);
      url.searchParams.set('start_date', startISO);
      url.searchParams.set('end_date', endISO);
      url.searchParams.set('sort_by', sortBy);
      url.searchParams.set('sort_dir', sortDir);
      if (search && search.trim()) url.searchParams.set('q', search.trim());
      if (jobTypeFilter) url.searchParams.set('job_type', jobTypeFilter);
      const resp = await fetch(url.toString(), { headers: { 'Authorization': `Bearer ${adminToken}` } });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const urlBlob = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlBlob;
      a.download = `job_reports_${startDate}_${endDate}.xlsx`;
      a.click();
      URL.revokeObjectURL(urlBlob);
    } catch (e) {
      console.error(e);
      alert(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const pages = Math.ceil(total / pageSize) || 1;

  const headerCell = (label, key) => (
    <div
      style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280', cursor: 'pointer', userSelect: 'none' }}
      onClick={() => {
        const nextDir = sortBy === key && sortDir === 'desc' ? 'asc' : 'desc';
        setSortBy(key);
        setSortDir(nextDir);
        fetchPage(page, key, nextDir);
      }}
    >
      {label} {sortBy === key ? (sortDir === 'desc' ? '▼' : '▲') : ''}
    </div>
  );

  const staticHeaderCell = (label) => (
    <div style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>{label}</div>
  );

  const copyToClipboard = async (text, type, jobId) => {
    try {
      await navigator.clipboard.writeText(text || '');
      if (type === 'id') {
        setCopiedId(jobId);
        setTimeout(() => setCopiedId(null), 1200);
      } else if (type === 'url') {
        setCopiedUrl(jobId);
        setTimeout(() => setCopiedUrl(null), 1200);
      } else if (type === 'ampleleads_id') {
        setCopiedAmpleLeadsId(jobId);
        setTimeout(() => setCopiedAmpleLeadsId(null), 1200);
      }
    } catch (e) { console.error('Copy failed', e); }
  };

  const [selectedJobType, setSelectedJobType] = useState('apollo'); // track job type for force-fail

  const handleForceFailJob = async (jobId, jobType = 'apollo') => {
    // Open modal to get failure reason
    setSelectedJobId(jobId);
    setSelectedJobType(jobType);
    setFailureReason('');
    setShowForceFailModal(true);
  };

  const confirmForceFailJob = async () => {
    if (!failureReason.trim()) {
      alert('Please provide a reason for failure');
      return;
    }

    const jobId = selectedJobId;

    try {
      setForceFailing(jobId);
      setShowForceFailModal(false);
      const adminToken = localStorage.getItem('admin_token');

      const response = await fetch(
        `${API_BASE_URL}/api/admin/scraper-admin/jobs/${jobId}/force-fail?job_type=${selectedJobType}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            failure_reason: failureReason.trim()
          })
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        alert(result.message || 'Job force-failed successfully');
        fetchPage(); // Refresh table
      } else {
        alert(result.detail || result.message || 'Failed to force-fail job');
      }
    } catch (err) {
      console.error('Error force-failing job:', err);
      alert('Error force-failing job. Please try again.');
    } finally {
      setForceFailing(null);
      setSelectedJobId(null);
      setSelectedJobType('apollo');
      setFailureReason('');
    }
  };

  const handleRecheckJobStatus = async (jobId) => {
    if (!window.confirm('Re-check this job status from AmpleLeads? This will sync the latest status.')) {
      return;
    }

    try {
      setRecheckingJobId(jobId);
      const adminToken = localStorage.getItem('admin_token');

      const response = await fetch(
        `${API_BASE_URL}/api/admin/scraper-admin/jobs/${jobId}/recheck-status`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        // Build detailed message
        let message = result.message;
        if (result.details && result.details.refund_amount) {
          message += ` Refunded: ${result.details.refund_amount} credits.`;
        }
        alert(message);
        fetchPage(); // Refresh table
      } else {
        alert(result.detail || result.message || 'Failed to recheck job status');
      }
    } catch (err) {
      console.error('Error rechecking job status:', err);
      alert('Error rechecking job status. Please try again.');
    } finally {
      setRecheckingJobId(null);
    }
  };

  const handleDeductCredits = async (jobId) => {
    if (!window.confirm('Deduct credits for this completed job? This will retroactively charge the user for the contacts received.')) {
      return;
    }

    try {
      setDeductingCreditsJobId(jobId);
      const adminToken = localStorage.getItem('admin_token');

      const response = await fetch(
        `${API_BASE_URL}/api/admin/scraper-admin/jobs/${jobId}/retroactive-charge`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        alert(result.message || 'Credits deducted successfully');
        fetchPage(); // Refresh table
      } else {
        alert(result.detail || result.message || 'Failed to deduct credits');
      }
    } catch (err) {
      console.error('Error deducting credits:', err);
      alert('Error deducting credits. Please try again.');
    } finally {
      setDeductingCreditsJobId(null);
    }
  };

  // Grid template - 13 columns including Type, AmpleLeads ID and wider action column
  const gridTemplate = '0.7fr 1.2fr 0.8fr 2.5fr 0.7fr 0.8fr 0.8fr 0.9fr 1.5fr 1.1fr 1.1fr 1fr 1fr';

  return (
    <Card>
      <Card.Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Card.Title>Job Reports</Card.Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }} />
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }} />
            <select value={jobTypeFilter} onChange={(e) => setJobTypeFilter(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}>
              <option value="">All Jobs</option>
              <option value="apollo">Apollo</option>
              <option value="lead_finder">Lead Finder</option>
            </select>
            <div style={{ position: 'relative' }}>
              <input
                placeholder="Search email, URL, list name, status, job id"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchPage(1, sortBy, sortDir, pageSize, e.target.value, startDate, endDate, jobTypeFilter); }}
                style={{ padding: '8px 28px 8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, width: 340 }}
              />
              <Search size={14} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            </div>
            <Button variant="secondary" onClick={() => fetchPage(1, sortBy, sortDir, pageSize, search, startDate, endDate, jobTypeFilter)} icon={<RefreshCw size={14} />}>Filter</Button>
            <Button variant="secondary" onClick={exportXlsx} disabled={exporting} icon={<Download size={14} />}>
              {exporting ? 'Exporting…' : 'Export XLSX'}
            </Button>
          </div>
        </div>
      </Card.Header>
      <Card.Content>
        <div style={{ overflowX: 'auto' }}>
          {/* Header as CSS Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: gridTemplate, borderBottom: '1px solid #e5e7eb', minWidth: 1400 }}>
            {headerCell('Date', 'created_at')}
            {staticHeaderCell('User Email')}
            {staticHeaderCell('Job ID')}
            {staticHeaderCell('URL / List Name')}
            {staticHeaderCell('Type')}
            {headerCell('Requested', 'number_of_contacts')}
            {headerCell('Scraped', 'result_count')}
            {headerCell('Status', 'status')}
            {staticHeaderCell('Error')}
            {staticHeaderCell('Started')}
            {staticHeaderCell('Completed')}
            {staticHeaderCell('AmpleLeads ID')}
            {staticHeaderCell('Action')}
          </div>

          {/* Rows */}
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>Loading...</div>
          ) : items.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No jobs found</div>
          ) : (
            items.map(job => (
              <div key={job.job_id} style={{ display: 'grid', gridTemplateColumns: gridTemplate, borderBottom: '1px solid #f3f4f6', alignItems: 'center', minWidth: 1400 }}>
                <div style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{job.date}</div>
                <div style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.user_email}</div>
                <div style={{ padding: '10px 12px', fontSize: 12, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span title={job.job_id}>{(job.job_id || '').slice(0, 8)}...</span>
                  <button onClick={() => copyToClipboard(job.job_id, 'id', job.job_id)} title={copiedId === job.job_id ? 'Copied!' : 'Click to copy job id'} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: copiedId === job.job_id ? '#16a34a' : '#6b7280' }}>
                    <Copy size={14} />
                  </button>
                </div>
                {/* URL column for Apollo, List Name for Lead Finder */}
                <div style={{ padding: '10px 12px', fontSize: 12, color: '#111827', overflow: 'hidden' }} title={job.job_type === 'lead_finder' ? job.list_name : job.apollo_url}>
                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {job.job_type === 'lead_finder' ? job.list_name : job.apollo_url}
                  </span>
                  {(job.job_type === 'apollo' && job.apollo_url && !safeApolloHref(job.apollo_url)) && (
                    <span style={{ display: 'block', fontSize: 11, color: '#b91c1c', marginTop: 2 }}>
                      Not a valid Apollo.io link - do not open
                    </span>
                  )}
                  {(job.job_type === 'apollo' && job.apollo_url) && (
                    <button onClick={() => copyToClipboard(job.apollo_url, 'url', job.job_id)} title={copiedUrl === job.job_id ? 'Copied!' : 'Click to copy URL'} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: copiedUrl === job.job_id ? '#16a34a' : '#6b7280', marginTop: 4 }}>
                      <Copy size={14} />
                    </button>
                  )}
                </div>
                {/* Type badge column */}
                <div style={{ padding: '10px 12px' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 600, color: job.job_type === 'lead_finder' ? '#7c3aed' : '#0369a1', backgroundColor: job.job_type === 'lead_finder' ? '#f3e8ff' : '#e0f2fe' }}>
                    {job.job_type === 'lead_finder' ? 'Lead Finder' : 'Apollo'}
                  </span>
                </div>
                <div style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{job.contacts_requested}</div>
                <div style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{job.contacts_scraped}</div>
                <div style={{ padding: '10px 12px' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, color: job.status === 'completed' ? '#166534' : job.status === 'failed' ? '#991b1b' : '#92400e', backgroundColor: job.status === 'completed' ? '#dcfce7' : job.status === 'failed' ? '#fee2e2' : '#fef3c7' }}>{job.status}</span>
                </div>
                <div style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={job.error_message}>{job.error_message}</div>
                <div style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{job.requested_timestamp}</div>
                <div style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{job.completion_timestamp}</div>
                <div style={{ padding: '10px 12px', fontSize: 11, color: '#111827' }}>
                  {job.ampleleads_job_id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span title={job.ampleleads_job_id} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {job.ampleleads_job_id.slice(0, 12)}...
                      </span>
                      <button
                        onClick={() => copyToClipboard(job.ampleleads_job_id, 'ampleleads_id', job.job_id)}
                        title={copiedAmpleLeadsId === job.job_id ? 'Copied!' : 'Click to copy AmpleLeads ID'}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          color: copiedAmpleLeadsId === job.job_id ? '#16a34a' : '#6b7280',
                          flexShrink: 0
                        }}
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  ) : (
                    <span style={{ color: '#6b7280' }}>-</span>
                  )}
                </div>
                <div style={{ padding: '10px 12px', display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                  {(job.status === 'running' || job.status === 'pending') && (
                    <button
                      onClick={() => handleForceFailJob(job.job_id, job.job_type || 'apollo')}
                      disabled={forceFailing === job.job_id}
                      title="Force fail this stuck job"
                      style={{
                        padding: '4px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#dc2626',
                        backgroundColor: forceFailing === job.job_id ? '#f3f4f6' : '#fff',
                        border: '1px solid #dc2626',
                        borderRadius: 6,
                        cursor: forceFailing === job.job_id ? 'not-allowed' : 'pointer',
                        opacity: forceFailing === job.job_id ? 0.5 : 1
                      }}
                    >
                      {forceFailing === job.job_id ? 'Failing...' : 'Force Fail'}
                    </button>
                  )}
                  {(job.status === 'failed' && job.scraping_service === 'ampleleads') && (
                    <button
                      onClick={() => handleRecheckJobStatus(job.job_id)}
                      disabled={recheckingJobId === job.job_id}
                      title="Re-check status from AmpleLeads"
                      style={{
                        padding: '4px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#2563eb',
                        backgroundColor: recheckingJobId === job.job_id ? '#f3f4f6' : '#fff',
                        border: '1px solid #2563eb',
                        borderRadius: 6,
                        cursor: recheckingJobId === job.job_id ? 'not-allowed' : 'pointer',
                        opacity: recheckingJobId === job.job_id ? 0.5 : 1
                      }}
                    >
                      {recheckingJobId === job.job_id ? 'Checking...' : 'Re-check'}
                    </button>
                  )}
                  {(job.status === 'completed' && job.scraping_service === 'ampleleads' && job.contacts_scraped > 0) && (
                    <button
                      onClick={() => handleDeductCredits(job.job_id)}
                      disabled={deductingCreditsJobId === job.job_id}
                      title="Retroactively deduct credits for this completed job"
                      style={{
                        padding: '4px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#d97706',
                        backgroundColor: deductingCreditsJobId === job.job_id ? '#f3f4f6' : '#fff',
                        border: '1px solid #d97706',
                        borderRadius: 6,
                        cursor: deductingCreditsJobId === job.job_id ? 'not-allowed' : 'pointer',
                        opacity: deductingCreditsJobId === job.job_id ? 0.5 : 1
                      }}
                    >
                      {deductingCreditsJobId === job.job_id ? 'Deducting...' : 'Deduct Credits'}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Page {page} of {Math.ceil(total / pageSize) || 1} • {total} total</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={() => { if (page > 1) { const np = page - 1; setPage(np); fetchPage(np); } }} disabled={page === 1} icon={<ChevronLeft size={14} />}>Prev</Button>
            <Button variant="secondary" onClick={() => { if (page < (Math.ceil(total / pageSize) || 1)) { const np = page + 1; setPage(np); fetchPage(np); } }} disabled={page >= (Math.ceil(total / pageSize) || 1)} icon={<ChevronRight size={14} />}>Next</Button>
          </div>
        </div>
      </Card.Content>

      {/* Force Fail Modal */}
      <Modal
        isOpen={showForceFailModal}
        onClose={() => {
          setShowForceFailModal(false);
          setSelectedJobId(null);
          setFailureReason('');
        }}
        title="Force Fail Job"
      >
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
            This will refund all credits and mark the job as failed. Please provide a reason that will be displayed to the user.
          </p>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
            Failure Reason <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <textarea
            value={failureReason}
            onChange={(e) => setFailureReason(e.target.value)}
            placeholder="e.g., Apollo.io did not return any results for this search"
            rows={4}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none'
            }}
            onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button
            variant="secondary"
            onClick={() => {
              setShowForceFailModal(false);
              setSelectedJobId(null);
              setFailureReason('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmForceFailJob}
            disabled={!failureReason.trim()}
            style={{
              backgroundColor: !failureReason.trim() ? '#f3f4f6' : '#dc2626',
              color: !failureReason.trim() ? '#9ca3af' : '#ffffff',
              cursor: !failureReason.trim() ? 'not-allowed' : 'pointer'
            }}
          >
            Force Fail Job
          </Button>
        </div>
      </Modal>
    </Card>
  );
};

export default PagedJobReportsTable;